<template>
  <Modal
    :value="value"
    width="auto"
    class="edition-dialog"
    smallHeader
    @input="$emit('input', !value)"
  >
    <template #modal-header>
      <Heading class="edition-dialog--custom-title">
        {{ dayData.title }}
      </Heading>
    </template>

    <!-- CONTEÚDO -->
    <template #body-content>
      <div class="edition-dialog--content">
        <div class="edition-dialog--content__scroll">
          <Heading class="edition-dialog--title">
            {{ hotel }}
          </Heading>
          <!-- MARCAR TODOS -->
          <b-row class="check-all-row__stick">
            <b-col class="check-all">
              <InputCheckbox
                :checked="allClosedChecked"
                label="Fechar todos"
                @change="toggleCloseAll"
              />

              <InputCheckbox
                :checked="allCtaChecked"
                label="CTA todos"
                @change="toggleCtaAll"
              />

              <InputCheckbox
                :checked="allCtdChecked"
                label="CTD todos"
                @change="toggleCtdAll"
              />
            </b-col>
          </b-row>

          <!-- TABELA -->
          <component
            :is="currentTableValue"
            :rooms="hotelData.quartos"
            :date="dayData.date"
            class="edition-table"
          />

          <!-- RESTRIÇÕES MIN. DE NOITES -->
          <b-row class="restriction-all">
            <b-col>
              <FormField label="Restrição minimo de noites">
                <b-row>
                  <b-col>
                    <Select
                      v-model="minNightsTypeToAll"
                      :options="[
                        { text: 'Chegando em', value: 1 },
                        { text: 'Passando por', value: 2 }
                      ]"
                      class="select__borderless"
                    />

                    <!-- DIAS -->
                    <input
                      v-model="minNightsDaysToAll"
                      type="number"
                      class="general-min-nights"
                      placeholder="00"
                      maxlength="2"
                      max="99"
                      min="0"
                      @focus="$event.target.select()"
                    />
                    <span>Dias</span>

                    <Button
                      size="large"
                      class="apply-to-all"
                      @click="applyMinNightsToAll"
                      >Aplicar em todos</Button
                    >
                  </b-col>
                </b-row>
              </FormField>
            </b-col>
          </b-row>
        </div>
      </div>
    </template>

    <!-- AÇÕES -->
    <template #actions>
      <div class="edition-dialog--actions">
        <Button
          class="edition-dialog--action-btn"
          layout="outline"
          size="large"
          @click="$emit('input', false)"
          >Fechar</Button
        >
        <Button
          :disabled="disableSave"
          class="edition-dialog--action-btn"
          size="large"
          @click="saveDaily"
          >Salvar alterações</Button
        >
      </div>
    </template>
  </Modal>
</template>

<script>
/**
 * Componente de edição das configurações dos quartos do hotel
 * para um dia específico do calendário
 */
import { mapGetters } from 'vuex'
import { parseISO } from 'date-fns'
import { formatDate } from '@/helpers/date'
import Modal from '@/components/_base/patterns/Modal'
import Heading from '@/components/_base/elements/Heading'
import Button from '@/components/_base/elements/Button'
import InputCheckbox from '@/components/_base/elements/InputCheckbox'
import FormField from '@/components/_base/patterns/FormField'
import Select from '@/components/_base/elements/Select'
import AbsoluteTable from '@/components/hotel/allotmentAndRatesCalendar/AbsoluteTable'
import FloatingTable from '@/components/hotel/allotmentAndRatesCalendar/FloatingTable'

export default {
  name: 'DayEditionModal',
  components: {
    Modal,
    Heading,
    Button,
    InputCheckbox,
    FormField,
    Select,
    AbsoluteTable,
    FloatingTable
  },
  props: {
    /**
     * Dados da configuração do dia
     *
     * {
     *    title: String
     * }
     */
    dayData: {
      type: Object,
      required: true
    },
    /** Se deve mostrar a modal */
    value: {
      type: Boolean,
      default: false
    },
    /** Tarifa selecionda */
    rate: {
      type: String,
      default: ''
    },
    hotel: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      closeAll: true /** Fechar para todos */,
      ctaAll: true /** Checkin fechado para todos */,
      ctdAll: true /** Checkout fechado para todos */,
      minNightsDaysToAll: 0 /** Mínimo de noites para todos */,
      minNightsTypeToAll: {
        text: 'Chegando em',
        value: 1
      } /** Tipo de mínimo de noites para todos */,
      /** Dados do hotel */
      hotelData: {
        data: null,
        codigoHotel: null,
        quartos: []
      },
      disableSave: true /** Desabilita o botão de salvar */,
      savingDaily: false /** Se uma requisição para salvar já está sendo feita */
    }
  },
  computed: {
    allClosedChecked() {
      return this.hotelData.quartos.every((item) => item.fechado === true)
    },
    allCtaChecked() {
      return this.hotelData.quartos.every((item) => item.cta === true)
    },
    allCtdChecked() {
      return this.hotelData.quartos.every((item) => item.ctd === true)
    },
    /** Retorna o tipo da tabela que deve ser apresentada */
    currentTableValue() {
      if (this.isAbsoluteRate) {
        return 'AbsoluteTable'
      }
      return 'FloatingTable'
    },
    ...mapGetters('hotel', ['isAbsoluteRate', 'getHotelCode'])
  },
  watch: {
    /** Observa o controlador de visualização */
    value(newVal) {
      /** Se atualizou o valor faz a requisição */
      if (newVal) {
        this.$calendar
          .getDailyRate(
            this.getHotelCode,
            formatDate(new Date(this.dayData.date), 'yyyy-MM-dd'),
            this.rate
          )
          .then(async (response) => {
            this.hotelData.codigoHotel = this.getHotelCode
            this.hotelData.data = formatDate(
              parseISO(response.data[0].data),
              'yyyy-MM-dd'
            )
            this.hotelData.quartos = response.data

            /**
             * Verifica as opções de retrições de fechamento
             * para incializar as seleções de marcar todos os quartos
             */
            this.hotelData.quartos.forEach((item) => {
              item.desconto = item.desconto === 0 ? null : item.desconto
              if (!item.fechado) {
                this.closeAll = false
              }
              if (!item.cta) {
                this.ctaAll = false
              }
              if (!item.ctd) {
                this.ctdAll = false
              }
            })

            /** Espera para atualizar no próximo ciclo de renderização */
            await this.$nextTick()

            /** Desabilita o botão de salvar ao carregar uma nova data */
            this.disableSave = true
          })
      }
    },
    /**
     * Observa mudanças em profundidade para
     * habilitar o botão de salvar
     */
    hotelData: {
      handler() {
        this.disableSave = false
      },
      deep: true
    }
  },
  methods: {
    /** Modifica o array de quartos de acordo com os filtro de fechado para checkin */
    toggleCtaAll(checked) {
      this.hotelData.quartos.map((item) => {
        item.cta = checked

        return item
      })
    },
    /** Modifica o array de quartos de acordo com os filtro de fechado para checkout */
    toggleCtdAll(checked) {
      this.hotelData.quartos.map((item) => {
        item.ctd = checked

        return item
      })
    },
    /** Modifica o array de quartos de acordo com os filtro de fechado */
    toggleCloseAll(checked) {
      this.hotelData.quartos.map((item) => {
        item.fechado = checked

        return item
      })
    },
    /** Aplica as restrições de mínimo de noites para todos os quartos */
    applyMinNightsToAll() {
      this.hotelData.quartos.map((item) => {
        item.minimoNoites = this.minNightsDaysToAll
        item.tipoMinimoNoites = this.minNightsTypeToAll.value

        return item
      })
    },
    /** Salva as alterações para a data selecionada */
    saveDaily() {
      if (this.savingDaily) return
      this.savingDaily = true

      const data = JSON.parse(JSON.stringify(this.hotelData))

      data.quartos.map((item) => {
        item.minimoNoites = +item.minimoNoites
        item.CTA = item.cta
        item.CTD = item.ctd
        item.quantidadeAllotment = +item.quantidadeAllotment
        item.quantidadeDisponivel = +item.quantidadeDisponivel
        item.codigoTarifa = this.rate
        item.desconto = +item.desconto
        item.valoresPax = item.valoresPax.map((pax) => {
          pax.valor = +pax.valor
          return pax
        })

        delete item.cta
        delete item.ctd

        return item
      })

      this.$calendar
        .updateDaily(data)
        .then(() => {
          this.$bus.$emit('show-alert-chip', {
            message: 'Atualizado com sucesso.'
          })

          this.$emit('input', false)
          this.$emit('savedData', this.hotelData)

          this.savingDaily = false
        })
        .catch(() => {
          // eslint-disable-next-line
          this.savingDaily = false
        })
    }
  }
}
</script>

<style lang="scss" scoped>
.edition-dialog {
  &--title {
    font-size: 26px;
    line-height: 35px;
    margin-bottom: 30px;
  }

  &--custom-title {
    margin: 0;
  }

  &--content {
    padding: {
      left: 30px;
      right: 30px;
      top: 35px;
      bottom: 10px;
    }
    overflow-x: hidden;

    &__scroll {
      overflow-x: auto;
      @include scrollbars(5px, $gray-mid, $white);
    }

    .check-all {
      display: flex;
      &-row__stick {
        position: sticky;
        left: -10px;
      }
    }

    .edition-table {
      padding-top: 33px;
      overflow-x: visible;
    }

    ::v-deep .input-checkbox {
      margin-right: 24px;

      &--label {
        width: auto;
      }
    }

    .restriction-all {
      margin-top: 35px;
      position: sticky;
      left: -10px;
      padding-bottom: 30px;

      input {
        border: none;
        margin: 0 15px;
        padding-left: 10px;
        font-size: font-size('size-medium');
        @include placeholder {
          color: $gray-mid;
        }
      }

      span {
        font-size: font-size('size-medium');
      }

      .apply-to-all {
        margin-left: 20px;
      }

      .select__borderless {
        width: 140px;
        display: inline-block;

        ::v-deep .select--button {
          border: none;
        }
      }
    }
  }

  &--actions {
    display: flex;
    justify-content: flex-end;
    width: 100%;
  }

  &--action-btn {
    margin-left: 10px;
  }
}

.general-min-nights {
  @include reset-input();
}

::v-deep .modal-component--close-button {
  padding: {
    top: 13px;
    bottom: 13px;
  }
  height: 12px;

  svg.icon {
    width: 12px;
    height: 12px;
  }
}

.separate-line {
  display: none;
}
</style>
